// frontend/src/pages/portfolio/sections/HowItWorksSection.tsx
import React from "react";
import { Compass } from "lucide-react";
import SectionEyebrow from "../components/SectionEyebrow";
import {
  cardHover,
  container,
  sectionHeading,
  sectionLede,
  sectionReveal,
} from "../components/styles";

const steps = [
  {
    step: "01",
    title: "Register your organization",
    body: "Sign up free in under a minute — just a name, email, and password. No credit card, no sales call.",
  },
  {
    step: "02",
    title: "Invite your team",
    body: "Add employees from the admin dashboard. Each gets login credentials to clock in from web or desktop.",
  },
  {
    step: "03",
    title: "Track time, your way",
    body: "Employees clock in from the browser or the desktop app. Activity, sessions, and screenshots sync automatically.",
  },
  {
    step: "04",
    title: "Review and act",
    body: "Admins see live dashboards, calendar views, and export-ready reports. Turn on AI summaries anytime — only pay for what you use.",
  },
];

const HowItWorksSection: React.FC = () => {
  return (
    <section
      id="how-it-works"
      className="border-t border-primary-200/30 bg-white py-16 sm:py-20"
      aria-labelledby="how-heading"
    >
      <div className={container}>
        <div className={`max-w-2xl ${sectionReveal}`}>
          <SectionEyebrow icon={Compass}>How it works</SectionEyebrow>
          <h2 id="how-heading" className={`mt-4 ${sectionHeading}`}>
            From sign-up to tracking in minutes
          </h2>
          <p className={sectionLede}>
            Four steps from "let&apos;s try it" to a team that&apos;s already
            clocking in.
          </p>
        </div>

        <ol className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map(({ step, title, body }, i) => (
            <li
              key={step}
              className={`relative rounded-2xl border border-gray-200 bg-white p-6 shadow-card ring-1 ring-primary-900/[0.02] ${cardHover}`}
            >
              {/* Decorative numeral — kept muted but at primary-500, which
                  clears WCAG large-text contrast (3:1 for 36px bold). The
                  accessible step number is still provided via the sr-only span
                  below, so aria-hidden keeps this out of the screen-reader tree. */}
              <span
                className="font-display text-4xl font-bold tabular-nums text-primary-500"
                aria-hidden="true"
              >
                {step}
              </span>
              <h3 className="mt-2 font-display text-lg font-semibold text-primary-950">
                <span className="sr-only">Step {i + 1}: </span>
                {title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-600">{body}</p>
              {i < steps.length - 1 && (
                <span
                  className="absolute -right-4 top-1/2 hidden h-px w-8 -translate-y-1/2 bg-gradient-to-r from-primary-200 to-transparent lg:block"
                  aria-hidden
                />
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default HowItWorksSection;
